import type { ApplicationContext } from '@wix/editor-platform-contexts';
import type {
  WorkspaceHost as WorkspaceHostType,
  AppsNamespace,
  ToolsNamespace,
} from '@wix/workspace';

export class Workspace implements NonNullable<WorkspaceHostType['workspace']> {
  constructor(private applicationContext: Promise<ApplicationContext>) {}

  private async workspaceAPI(): Promise<
    NonNullable<WorkspaceHostType['workspace']>
  > {
    const context = await this.applicationContext;
    const privateAPI = context.getPrivateAPI();
    return await privateAPI.workspace;
  }

  private async appsAPI(): Promise<AppsNamespace> {
    const workspace = await this.workspaceAPI();
    if (!workspace.apps) {
      throw new Error(
        'apps namespace is not available — host has not implemented it',
      );
    }
    return workspace.apps;
  }

  private async toolsAPI(): Promise<ToolsNamespace> {
    const workspace = await this.workspaceAPI();
    if (!workspace.tools) {
      throw new Error(
        'tools namespace is not available — host has not implemented it',
      );
    }
    return workspace.tools;
  }

  getContextIds: NonNullable<WorkspaceHostType['workspace']>['getContextIds'] =
    () => {
      throw new Error('Not implemented yet');
    };

  openItemSelector: NonNullable<
    WorkspaceHostType['workspace']
  >['openItemSelector'] = (_args) => {
    throw new Error('Not implemented yet');
  };

  apps: AppsNamespace = {
    startInstallFlow: async (args) => {
      const apps = await this.appsAPI();
      return apps.startInstallFlow(args);
    },
    startPurchasePlanFlow: async (args) => {
      const apps = await this.appsAPI();
      return apps.startPurchasePlanFlow(args);
    },
    startUpdateFlow: async (args) => {
      const apps = await this.appsAPI();
      return apps.startUpdateFlow(args);
    },
    openContactSupport: async (args) => {
      const apps = await this.appsAPI();
      return apps.openContactSupport(args);
    },
  };

  tools: ToolsNamespace = {
    editWithWixel: async (args) => {
      const tools = await this.toolsAPI();
      return tools.editWithWixel(args);
    },
  };
}
export class WorkspaceHost {
  #api: Workspace;

  getAPI(): Workspace {
    return this.#api;
  }

  constructor(applicationContext: Promise<ApplicationContext>) {
    this.#api = new Workspace(applicationContext);
  }
}
